/* ============================================
   Markup del partner — de nuestro costo a su precio, de una vez

   Hasta acá el partner ponía su precio ítem por ítem, y sólo en las
   cotizaciones que armaba él. Las que armamos nosotros —Nordic, Grupo
   Central— las abría, veía NUESTROS precios y no tenía dónde cargar los
   suyos: el clic en la lista llevaba al documento público, no al editor.

   Este panel es ese lugar. Tres formas de decir lo mismo, porque son las tres
   formas en que se piensa un precio: «le sumo 60%», «le sumo 5.000», «quiero
   que quede en 20.000». Las tres terminan en un multiplicador.

   El panel NO es una pantalla aparte ni un módulo nuevo: vive dentro del
   editor que el partner ya usa. Un constructor separado habría duplicado el
   editor entero —ítems, secciones, cronograma— para cambiar un número.

   La aritmética no está acá: vive en `src/lib/markup.ts`, que es también de
   donde el backend saca el factor para los precios escritos en la prosa.
   ============================================ */
import { useMemo, useState, type ReactNode } from 'react'
import type { CotizacionItemsEstandar, Markup, MarkupTipo } from '@/lib/types'
import {
  aplicarMarkup, construirMarkup, costoBase, itemsEnOtraMoneda, itemsFijados, resumenMarkup,
} from '@/lib/markup'
import { formatCurrency, r2 } from '@/lib/utils'
import { Badge } from '@/components/ui'
import { IconAlertCircle, IconCheckCircle, IconRefresh, IconUndo } from '@/components/icons/Icons'

/*
  Los tres modos van como ICONO y no como palabra: son tres formas de decir
  lo mismo y el texto de los tres compite por la misma franja. El icono se
  reconoce de un vistazo y el nombre queda en el `title` para quien dude.
*/
const MODOS: { tipo: MarkupTipo; label: string; ayuda: string; icono: ReactNode }[] = [
  {
    tipo: 'porcentaje', label: 'Porcentaje',
    ayuda: 'Cuánto le sumas encima a lo que nos pagas',
    icono: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>,
  },
  {
    tipo: 'monto', label: 'Monto',
    ayuda: 'Cuánto ganas en total con esta cotización',
    icono: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  },
  {
    tipo: 'final', label: 'Precio final',
    ayuda: 'Cuánto quieres que pague tu cliente',
    icono: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  },
]

interface Props {
  items: CotizacionItemsEstandar[]
  moneda: string
  markup: Markup | null
  /** Devuelve el markup y los ítems ya recalculados: el editor los adopta. */
  onAplicar: (markup: Markup, items: CotizacionItemsEstandar[]) => void
  /** Vuelve a nuestros precios: borra el markup y deja los ítems al costo. */
  onQuitar: () => void
  /** La tabla de precios, que se dibuja dentro de esta misma tarjeta. */
  children?: ReactNode
}

export default function MarkupPartner({ items, moneda, markup, onAplicar, onQuitar, children }: Props) {
  const [tipo, setTipo] = useState<MarkupTipo>(markup?.tipo ?? 'porcentaje')
  const [valor, setValor] = useState<string>(markup ? String(markup.valor) : '')

  const base = useMemo(() => costoBase(items, moneda), [items, moneda])
  const fijados = useMemo(() => itemsFijados(items), [items])
  const otraMoneda = useMemo(() => itemsEnOtraMoneda(items, moneda), [items, moneda])

  /* Lo que se vería si aplicara AHORA, con lo que tiene tecleado. Se calcula
     con las mismas funciones que el guardado, así que lo que muestra es lo
     que va a quedar — no una aproximación de la pantalla. */
  const previo = useMemo(() => {
    const n = parseFloat(valor)
    if (!Number.isFinite(n)) return null
    const m = construirMarkup(tipo, n, items, moneda)
    return { markup: m, ...resumenMarkup(items, m, moneda) }
  }, [tipo, valor, items, moneda])

  // Sin ítems nuestros no hay nada que marcar: todo lo de esta cotización es
  // suyo y ya está al precio que él puso.
  if (base <= 0 && otraMoneda.length === 0 && fijados.length === 0) return null

  const aplicar = () => {
    if (!previo) return
    onAplicar(previo.markup, aplicarMarkup(items, previo.markup, moneda))
  }

  return (
    <div className="card markup-panel">
      <div className="card-header">
        <h3>Tu markup</h3>
        {markup
          ? <Badge variant="aceptada">Aplicado</Badge>
          : <Badge variant="warning">Sin aplicar — lleva nuestros precios</Badge>}
      </div>

      {/*
        Todo el control en UNA franja horizontal: modo, cuánto, el resultado y
        las dos acciones. En columna ocupaba media pantalla y empujaba la
        tabla de precios fuera de la vista, justo lo que hay que mirar
        mientras se decide el número.
      */}
      <div className="markup-panel__body">
        <div className="markup-barra">
          <div className="markup-barra__modos" role="group" aria-label="Cómo pones tu precio">
            {MODOS.map(m => (
              <button
                key={m.tipo}
                type="button"
                className={`markup-barra__modo ${tipo === m.tipo ? 'markup-barra__modo--on' : ''}`}
                onClick={() => setTipo(m.tipo)}
                title={m.label}
                aria-label={m.label}
                aria-pressed={tipo === m.tipo}
              >
                {m.icono}
              </button>
            ))}
          </div>

          <div className="markup-barra__campo">
            <label className="markup-barra__label" htmlFor="markup-valor">
              {MODOS.find(m => m.tipo === tipo)?.ayuda}
            </label>
            <div className="markup-barra__input">
              {tipo !== 'porcentaje' && <span className="markup-barra__unidad">{moneda}</span>}
              <input
                id="markup-valor"
                className="form-input"
                type="number"
                step="0.01"
                min="0"
                value={valor}
                onChange={e => setValor(e.target.value)}
                /* 0 y no un ejemplo: con la negrita del campo, un «60» de
                   muestra se lee como un número ya escrito, y el partner
                   cree que el markup viene puesto de fábrica. */
                placeholder="0"
              />
              {tipo === 'porcentaje' && <span className="markup-barra__unidad">%</span>}
            </div>
          </div>

          <div className="markup-barra__cifras">
            <div><span>Te cuesta</span><strong>{formatCurrency(base, moneda)}</strong></div>
            <div><span>Tu cliente paga</span><strong>{previo ? formatCurrency(previo.venta, moneda) : '—'}</strong></div>
            <div className="markup-barra__margen">
              <span>Te quedan</span>
              <strong>{previo ? `${formatCurrency(previo.margen, moneda)} (${r2(previo.pct)}%)` : '—'}</strong>
            </div>
          </div>

          {/* Sólo iconos: la franja no tiene sitio para dos botones con texto,
              y lo que hacen se entiende por la forma. */}
          <div className="markup-barra__acciones">
            <button
              type="button"
              className="btn btn-primary markup-barra__btn"
              onClick={aplicar}
              disabled={!previo}
              title={markup ? 'Actualizar los precios con este markup' : 'Aplicar a los ítems'}
              aria-label={markup ? 'Actualizar precios' : 'Aplicar a los ítems'}
            >
              <IconRefresh size={16} />
            </button>
            {markup && (
              <button
                type="button"
                className="btn btn-secondary markup-barra__btn"
                onClick={onQuitar}
                title="Volver a los precios base"
                aria-label="Volver a los precios base"
              >
                <IconUndo size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Los dos casos en que el markup no alcanza a todo. Se dicen siempre
            que existen, no sólo al aplicar: enterarse después de guardar es
            enterarse cuando el documento ya salió. */}
        {fijados.length > 0 && (
          <p className="markup-panel__aviso">
            <IconAlertCircle />
            {fijados.length === 1
              ? '1 ítem tiene precio puesto a mano y el markup no lo toca.'
              : `${fijados.length} ítems tienen precio puesto a mano y el markup no los toca.`}
            {' '}Para que vuelvan a seguirlo, bórrales el precio y vuelve a aplicar.
          </p>
        )}
        {otraMoneda.length > 0 && (
          <p className="markup-panel__aviso">
            <IconAlertCircle />
            {otraMoneda.length === 1 ? '1 ítem nos lo pagas' : `${otraMoneda.length} ítems nos los pagas`}
            {' '}en otra moneda, así que su precio lo pones tú a mano: no hay factor que
            convierta una moneda en otra.
          </p>
        )}

        {/* La tabla de precios, DENTRO de esta misma tarjeta: poner el markup
            y ver qué le hace a cada línea es un solo gesto, y separarlos en
            dos tarjetas obligaba a mirar arriba y abajo. */}
        {children}

        {markup && (
          <p className="markup-panel__nota">
            <IconCheckCircle />
            El costo base queda guardado, así que puedes cambiar este número —o dar
            un descuento— cuantas veces haga falta sin perderlo.
          </p>
        )}
      </div>
    </div>
  )
}
